import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["previewContainer"]
  static values = {
    logoUrl: String
  }

  connect() {
    this.updatePreview()
  }

  updatePreview() {
    if (!this.hasPreviewContainerTarget) return

    const logoUrl = this.logoUrlValue
    if (!logoUrl) return

    // Get selected values
    const size = this.getSelectedValue('logo_size') || 'medium'
    const shape = this.getSelectedValue('logo_shape') || 'standard'
    const background = this.getSelectedValue('logo_background') || 'none'
    const border = this.getSelectedValue('logo_border') || 'none'
    const hover = this.getSelectedValue('logo_hover_effect') || 'none'

    // For circle shape, we need wrapper approach
    if (shape === 'circle') {
      const wrapperClasses = this.buildCircleWrapperClasses(size, background, border)
      const imgClasses = 'w-full h-full object-contain'

      this.previewContainerTarget.innerHTML = `
        <div class="${wrapperClasses}" style="flex-shrink: 0;">
          <img src="${logoUrl}" class="${imgClasses}" alt="Logo preview" style="display: block;" />
        </div>
      `
    } else {
      const classes = this.buildClasses(size, shape, background, border, hover)

      this.previewContainerTarget.innerHTML = `<img src="${logoUrl}" class="${classes}" alt="Logo preview" style="display: block;" />`
    }
  }

  buildCircleWrapperClasses(size, background, border) {
    const classes = []

    // Size - add both height and width for perfect square
    switch (size) {
      case 'small':
        classes.push('h-20 w-20') // 80px
        break
      case 'medium':
        classes.push('h-24 w-24') // 96px
        break
      case 'large':
        classes.push('h-28 w-28') // 112px
        break
      case 'extra-large':
        classes.push('h-32 w-32') // 128px
        break
      default:
        classes.push('h-24 w-24')
    }

    // Circle specific - base styling
    classes.push('rounded-full p-3 flex items-center justify-center relative z-10')

    // Background - only add one background class
    if (background !== 'none') {
      switch (background) {
        case 'white':
          classes.push('bg-white')
          break
        case 'light':
          classes.push('bg-white/20')
          break
        case 'dark':
          classes.push('bg-gray-900/20')
          break
        case 'colored':
          classes.push('bg-blue-600/20')
          break
      }
    } else {
      classes.push('bg-white')
    }

    // Border
    if (border !== 'none') {
      switch (border) {
        case 'thin':
          classes.push('border border-white/20')
          break
        case 'medium':
          classes.push('border-2 border-white/30')
          break
        case 'thick':
          classes.push('border-4 border-white/40')
          break
      }
    }

    return classes.join(' ')
  }

  getSelectedValue(name) {
    const radio = this.element.querySelector(`input[name="website[${name}]"]:checked`)
    const select = this.element.querySelector(`select[name="website[${name}]"]`)

    if (radio) return radio.value
    if (select) return select.value
    return null
  }

  buildClasses(size, shape, background, border, hover) {
    const classes = []

    // Size classes
    switch (size) {
      case 'small':
        classes.push('h-20') // 80px
        break
      case 'medium':
        classes.push('h-24') // 96px
        break
      case 'large':
        classes.push('h-28') // 112px
        break
      case 'extra-large':
        classes.push('h-32') // 128px
        break
      default:
        classes.push('h-24') // Default to medium (96px)
    }

    // Shape classes (circle is handled separately with wrapper)
    switch (shape) {
      case 'standard':
        classes.push('rounded')
        break
      case 'rounded':
        classes.push('rounded-lg')
        break
      case 'shadow':
        classes.push('rounded shadow-lg')
        break
      default:
        classes.push('rounded')
    }

    // Background classes
    if (background !== 'none') {
      switch (background) {
        case 'light':
          classes.push('bg-white/10 p-2')
          break
        case 'dark':
          classes.push('bg-gray-900/10 p-2')
          break
        case 'colored':
          classes.push('bg-blue-600/10 p-2')
          break
      }
    }

    // Border classes
    if (border !== 'none') {
      switch (border) {
        case 'thin':
          classes.push('border border-white/20')
          break
        case 'medium':
          classes.push('border-2 border-white/30')
          break
        case 'thick':
          classes.push('border-4 border-white/40')
          break
      }
    }

    // Hover effect classes
    if (hover !== 'none') {
      switch (hover) {
        case 'scale':
          classes.push('transition-transform hover:scale-105')
          break
        case 'glow':
          classes.push('transition-shadow hover:shadow-lg hover:shadow-white/20')
          break
        case 'fade':
          classes.push('transition-opacity hover:opacity-80')
          break
      }
    }

    // Base classes
    classes.push('w-auto object-contain')

    return classes.join(' ')
  }
}
